import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ErrorResponse, FilteredDropDownModelInit, FilteredDropDownRequest, FilteredDropDownResponse, ListModel, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { Guid } from "guid-typescript";
import { ActivityLog, ActivityLogFilesResponse, ActivityLogsSliceModel, CreateActivityLogRequest, initialActivityLogValue, UpdateActivityLogRequest, UploadActivityLogFilesRequest } from "./activity-logs-interface";
import { Activity } from "./activities-interface";
import { API_BASE_URL as baseUri } from "config/env";

export const GetActivityLogById = createAsyncThunk<ActivityLog, Guid, { rejectValue: RejectedValue }>(
    'Slice/ActivityLogsSlice/GetActivityLogById',
    async (logId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/activity-logs/get?logId=${logId}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ActivityLog;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetActivityLogsByEmployee = createAsyncThunk<ActivityLog[], Guid, { rejectValue: RejectedValue }>(
    'Slice/ActivityLogsSlice/GetActivityLogsByEmployee',
    async (employeeId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/activity-logs/get?employeeId=${employeeId}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ActivityLog[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateActivityLog = createAsyncThunk<Activity, CreateActivityLogRequest, { rejectValue: RejectedValue }>(
    'Slice/ActivityLogsSlice/CreateActivityLog',
    async (createActivityLogRequest: CreateActivityLogRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/activity-logs/create`, {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(createActivityLogRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Activity;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateActivityLog = createAsyncThunk<Activity, UpdateActivityLogRequest, { rejectValue: RejectedValue }>(
    'Slice/ActivityLogsSlice/UpdateActivityLog',
    async (updateActivityLogRequest: UpdateActivityLogRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/activity-logs/update`, {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(updateActivityLogRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Activity;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UploadActivityLogFile = createAsyncThunk<ActivityLogFilesResponse, UploadActivityLogFilesRequest, { rejectValue: RejectedValue }>(
    'Slice/ActivityLogsSlice/UploadActivityLogFile',
    async (uploadActivityLogFilesRequest: UploadActivityLogFilesRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/activity-logs/upload-files`, {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(uploadActivityLogFilesRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ActivityLogFilesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeleteActivityLogFile = createAsyncThunk<ActivityLogFilesResponse, Guid, { rejectValue: RejectedValue }>(
    'Slice/ActivityLogsSlice/DeleteActivityLogFile',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/activity-logs/delete-file?id=${id}`, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ActivityLogFilesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPartnersForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/ActivityLogsSlice/GetPartnersForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetContactsForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/ActivityLogsSlice/GetContactsForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);


const initialValue: ActivityLogsSliceModel = {
    activityLog: undefined,
    activityLogModel: initialActivityLogValue,
    activityLogsByEmployee: [],

    statusCode: "",
    message: "",
    showNotification: false,
    reloadData: false,

    isLoading: false,

    filteredPartnersList: FilteredDropDownModelInit,
    filteredContactsList: FilteredDropDownModelInit
}

export const ActivityLogsSlice = createSlice({
    name: 'ActivitiesSlice',
    initialState: initialValue,
    reducers: {
        resetActivityLogsSlice: (state) => {
            state = initialValue
        },
        resetActivityLogStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
        resetActivityLogModelAndLists: (state) => {
            state.activityLogModel = initialActivityLogValue;
            state.filteredContactsList = FilteredDropDownModelInit;
            state.filteredPartnersList = FilteredDropDownModelInit;
        },
        setSelectedPartners: (state, action: PayloadAction<ListModel<string>[]>) => {
            if (state.activityLogModel) {
                state.activityLogModel.associatedCompanies = action.payload
            }
        },
        setSelectedContacts: (state, action: PayloadAction<ListModel<string>[]>) => {
            if (state.activityLogModel) {
                state.activityLogModel.associatedContacts = action.payload
            }
        },
    },
    extraReducers(builder) {
        builder.addCase(GetActivityLogById.fulfilled, (state, action: PayloadAction<ActivityLog>) => {
            state.isLoading = false;
            state.activityLog = action.payload;
            state.activityLogModel.id = action.payload.id;
            state.activityLogModel.associatedCompanies = action.payload.companies;
            state.activityLogModel.associatedContacts = action.payload.contacts;
            state.activityLogModel.content = action.payload.content;
            state.activityLogModel.date = action.payload.date;
            state.activityLogModel.activityTypeId = action.payload.activityTypeId;
            state.activityLogModel.outcome = action.payload.outcome!;
            state.activityLogModel.files = action.payload.files;
            state.activityLogModel.name = action.payload.name ?? "";
            state.activityLogModel.source = action.payload.sourceId;
            state.activityLogModel.isIncoming = action.payload.isIncoming;
        })
            .addCase(GetActivityLogById.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetActivityLogById.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetActivityLogsByEmployee.fulfilled, (state, action: PayloadAction<ActivityLog[]>) => {
            state.isLoading = false;
            //state.statusCode = "200";
            state.activityLogsByEmployee = action.payload;
        })
            .addCase(GetActivityLogsByEmployee.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetActivityLogsByEmployee.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(CreateActivityLog.fulfilled, (state, action: PayloadAction<Activity>) => {
            state.isLoading = false;
            state.statusCode = "200";
            state.message = "The activity log is successfully created.";
            state.showNotification = true;
        })
            .addCase(CreateActivityLog.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateActivityLog.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(UpdateActivityLog.fulfilled, (state, action: PayloadAction<Activity>) => {
            state.isLoading = false;
            state.statusCode = "200";
            state.message = "The activity log is successfully updated.";
            state.showNotification = true;
        })
            .addCase(UpdateActivityLog.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateActivityLog.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(UploadActivityLogFile.fulfilled, (state, action: PayloadAction<ActivityLogFilesResponse>) => {
            state.isLoading = false;
            //state.message = "The note is successfully updated";
            state.statusCode = "200";
            //state.showNotification = true;
        })
            .addCase(UploadActivityLogFile.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UploadActivityLogFile.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(DeleteActivityLogFile.fulfilled, (state, action: PayloadAction<ActivityLogFilesResponse>) => {
            state.isLoading = false;
            //state.message = "The note is successfully updated";
            state.statusCode = "200";
            //state.showNotification = true;
        })
            .addCase(DeleteActivityLogFile.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeleteActivityLogFile.pending, state => {
                state.isLoading = true;
            })
        //Get Partners list    
        builder.addCase(GetPartnersForDropdown.pending, (state) => {
            state.filteredPartnersList.loading = true;
            state.filteredPartnersList.error = null;
        })
            .addCase(GetPartnersForDropdown.fulfilled, (state, action) => {
                state.filteredPartnersList.loading = false;
                state.filteredPartnersList.data = action.payload.data;
                state.filteredPartnersList.totalItems = action.payload.totalItems;
                state.filteredPartnersList.totalPages = action.payload.totalPages;
                state.filteredPartnersList.currentPage = action.payload.currentPage;
            })
            .addCase(GetPartnersForDropdown.rejected, (state, action) => {
                state.filteredPartnersList.loading = false;
                state.filteredPartnersList.error = action.payload?.message || 'Error fetching data';
            });
        //Get contacts list
        builder.addCase(GetContactsForDropdown.pending, (state) => {
            state.filteredContactsList.loading = true;
            state.filteredContactsList.error = null;
        })
            .addCase(GetContactsForDropdown.fulfilled, (state, action) => {
                state.filteredContactsList.loading = false;
                state.filteredContactsList.data = action.payload.data;
                state.filteredContactsList.totalItems = action.payload.totalItems;
                state.filteredContactsList.totalPages = action.payload.totalPages;
                state.filteredContactsList.currentPage = action.payload.currentPage;
            })
            .addCase(GetContactsForDropdown.rejected, (state, action) => {
                state.filteredContactsList.loading = false;
                state.filteredContactsList.error = action.payload?.message || 'Error fetching data';
            });
    }
});

export const { resetActivityLogsSlice, resetActivityLogStatusCode, setSelectedPartners, setSelectedContacts, resetActivityLogModelAndLists } = ActivityLogsSlice.actions;
export default ActivityLogsSlice.reducer;